import { InterfaceFactoryOptions } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/factory-options.interface';
import { isEditorEmpty } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/utils';
import { IEditorProps } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-props.interface';
import { EventArea } from '@tencent/alloy-editor';
import { RenderPostionType } from '@src/js/constant';
import { CANVAS_TABLE_CELL_ID_PREFIX } from '@src/slide/content-viewer/service/canvas-renderer/core/id-constants';
import { checkClickElement } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-pointer-cursor';
import { isCopyToImageOverSize } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-pc/copy-to-image/over-size-control';
import { TextDirectionType } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/text-style-type';
import { getSlideIndex } from '@src/slide/domain/ui-model/element-modeles/property/slide-number/operation';
import set from 'lodash/set';
import type { ITableCellModel, ITableModel } from '@src/slide/domain/ui-model/element-modeles/Interface/table-model.interface';
import { FORMAT_BRUSH_TEXT } from '@src/slide/application/service/format-brush/core/constant';
import { getBrushCursorStyle, needBrushCursor } from '@src/slide/application/service/format-brush/core/util';
import { BaseComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/base-component/base-component';
import { ComponentTypes } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/base-component/component-types';
import { TextEditor } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor';
import { FORBID_WEBGL_FILTER } from '@src/slide/infrastructure/module/dependent/config/renderer/switch';
import { CANVAS_PT_SCALE } from '@src/slide/infrastructure/module/independent/unit';
import { isShowTextFill } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/utils/link';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';
import { IReactionDisposer, reaction } from 'mobx';

export interface ITableCellEventHandlers {
    handleEditorMouseDown: (e) => void;
    handleEditorMouseUp: () => void;
    handleTextChange: ({ height }) => void;
    handleEnterEditing: () => void;
    handleExitEditing: () => void;
    handleChange: (editingCellData, fromSlateEditor) => void;
    beforeEditorValueChange: () => void;
    handleEditorStyleChange: ({ style }) => void;
    handleSelectionChange: () => void;
}

export class TableCellComponent extends BaseComponent {
    public cellModel: ITableCellModel;
    public textEditor: TextEditor | null;
    protected readonly factoryOptions: InterfaceFactoryOptions<ITableModel>;
    protected eventHandlers?: ITableCellEventHandlers;
    private animBrushDisposer?: IReactionDisposer;

    public constructor(factoryOptions: InterfaceFactoryOptions<ITableModel>, cellModel: ITableCellModel) {
        super();
        this.factoryOptions = factoryOptions;
        this.cellModel = cellModel;
        factoryOptions.logicService.setComponentLogic(factoryOptions, this);
    }

    public getUniqueId(): string {
        return `${this.cellModel?.key || ''}-${this.factoryOptions.region}-${this.getComponentType()}`;
    }

    public getComponentType(): string {
        return ComponentTypes.TABLECELL;
    }

    public setEventHandlers(eventHandlers: ITableCellEventHandlers) {
        this.eventHandlers = eventHandlers;
    }

    public dispose(): void {
        // 移除model中editor组件
        super.dispose();
        if (!this.factoryOptions.fromScale) { // 和注册逻辑对应
            this.cellModel.setEditorComponent(null);
        }
        this.factoryOptions.logicService.removeComponentLogic(this);
        this.animBrushDisposer?.();
    }

    public updateComponent(cellModel: ITableCellModel): void {
        this.cellModel = cellModel;
        this.id = `${CANVAS_TABLE_CELL_ID_PREFIX}-${cellModel.row}-${cellModel.col}`;
        this.renderTextEditor();
        this.observeAnimBrushManager();
    }

    public updateTextEditorCursor(cursor: string): void {
        this.textEditor?.updateCursor(cursor);
    }

    // 插入|更新 文本框
    public renderTextEditor(): void {
        if (!this.shouldShowEditor()) {
            if (this.textEditor) {
                this.textEditor.dispose();
                this.removeChild(this.textEditor.editorContainer);
                this.textEditor = null;
            }
            return;
        }
        const { fromScale, isEditable, region, getPlayerReduxStore, modelParams } = this.factoryOptions;
        const cellStateMap = modelParams?.updateState?.cellStateMap;
        const model = this.cellModel;
        // 缩略图默认更新，编辑区根据changeSet判断是否需要更新
        const shouldUpdate = !fromScale ? cellStateMap?.[model.key]?.shouldUpdate : true;
        const slideModel = model.slideRef || this.factoryOptions.slideModel;
        const editorProps: IEditorProps = {
            shouldUpdate,
            noStopMouseDown: true,
            paragraphList: model.paragraphList,
            textBodyStyle: model.textBodyStyle,
            model,
            modelKey: model.key,
            modelKeySuffix: `-${this.factoryOptions.region}`,
            textRect: this.getTextRect(model),
            getSelected: this.getSelected,
            editable: isEditable,
            eventArea: EventArea.all,
            slideNum: getSlideIndex(slideModel) + 1,
            datetime: this.factoryOptions.infrastructureService.initDatetime,
            selectable: !fromScale,
            shouldPreventTab: true,
            onEnterEditing: this.eventHandlers?.handleEnterEditing,
            onTextChange: this.eventHandlers?.handleTextChange,
            onExitEditing: this.eventHandlers?.handleExitEditing,
            onChange: this.eventHandlers?.handleChange,
            onBeforeValueChange: this.eventHandlers?.beforeEditorValueChange,
            onStyleChange: this.eventHandlers?.handleEditorStyleChange,
            getRenderTextExtraOption: this.getRenderTextExtraOption,
            handleSelectionChange: this.eventHandlers?.handleSelectionChange,
            linkColor: this.getLinkThemeColor(),
            resetShouldUpdate: () => {
                if (!fromScale) set(modelParams, 'updateState.shouldUpdate', true); // 不让左侧列表影响右侧逻辑
            },
            getApplication: this.factoryOptions.getApplication?.bind(this),
            cursor: this.getInitialCursor(),
            linkCursor: region === RenderPostionType.PLAYER ? 'pointer' : '',
            openLinkInPlay: (region === RenderPostionType.PLAYER) ? (linkData) => {
                this.factoryOptions.linkManager
                    ?.openLinkInPlay(getPlayerReduxStore?.(), linkData);
            } : null,
            onMouseUp: this.eventHandlers?.handleEditorMouseUp,
            onMouseDown: this.eventHandlers?.handleEditorMouseDown,
        };
        if (this.textEditor) {
            this.textEditor.updateEditorProps(editorProps);
        } else {
            this.textEditor = new TextEditor(editorProps, { ...this.factoryOptions });
            if (!fromScale) {
                model.setEditorComponent(this.textEditor);
            }
            this.addChild(this.textEditor.editorContainer);
        }
    }

    private getLinkThemeColor = () => {
        const { model } = this.factoryOptions;
        const slideModel = model.slideRef || this.factoryOptions.slideModel;
        return this.getLinkManager()?.linkUtils.getLinkColor(slideModel);
    };

    private getLinkManager() {
        return this.factoryOptions.linkManager;
    }

    private shouldShowEditor() {
        const { isEditable } = this.factoryOptions;
        const isDeadEditor = !isEditable && isEditorEmpty(this.cellModel.paragraphList);
        return !isDeadEditor;
    }

    private getTextRect(model) {
        const { displayHeight, textBodyStyle } = model;
        const textDirection = textBodyStyle?.wrapperStyle?.textDirection;
        const { style } = model.props;
        return {
            width: style.width,
            // 正常情况下取style.height即可，但为了兼容大表格模式，需要取displayHeight和style.height的max。
            // 不过直接取max在文字竖排模式会有问题，所以竖排时依旧用style.height
            height: [
                TextDirectionType.VERTICAL,
                TextDirectionType.ROTATE_90,
                TextDirectionType.ROTATE_270,
                TextDirectionType.WORD_ART_VERTICAL_RTL,
                TextDirectionType.WORD_ART_VERTICAL_LTR,
            ].includes(textDirection) ? style.height : Math.max(displayHeight, style.height),
            left: 0,
            top: 0,
        };
    }

    // 这部分初始值逻辑后面可以直接复用logic
    private getInitialCursor() {
        if (this.factoryOptions.region === 'player') {
            return checkClickElement(this.factoryOptions) ? 'pointer' : 'auto';
        }

        const { formatBrushManager, model } = this.factoryOptions;
        const { uniqueID } = model || {};

        // 如果实例化的时候，格式刷处于激活状态，需要设置为格式化光标
        const animBrushManager = (AppServiceProvider.getInstance()?.getAppServices() as IEditorAppServices)
            ?.editableServiceLayer?.animBrushManager;

        const animBrushState = animBrushManager?.animBrushState;
        const formatBrushState = formatBrushManager?.formatBrushState;
        if (needBrushCursor({ formatBrushManager, animBrushManager })) {
            return uniqueID === animBrushState?.originElId  ? 'text' : getBrushCursorStyle(formatBrushState?.data === FORMAT_BRUSH_TEXT);
        }

        return 'text';
    }

    private observeAnimBrushManager() {
        if (this.animBrushDisposer) return;
        const animBrushManager = (AppServiceProvider.getInstance()?.getAppServices() as IEditorAppServices)
            .editableServiceLayer?.animBrushManager;
        if (!animBrushManager) return;

        this.animBrushDisposer = reaction(() => {
            const animBrushState = (AppServiceProvider.getInstance()?.getAppServices() as IEditorAppServices)
                .editableServiceLayer?.animBrushManager.animBrushState;
            return [animBrushState];
        }, () => {
            const cursor = this.getInitialCursor();
            this.textEditor?.updateCursor(cursor);
        });
    }

    private getSelected = (): boolean => this.cellModel.tableModelRef?.selected;

    private getRenderTextExtraOption = option => ({
        forbidWebgl: FORBID_WEBGL_FILTER,
        effectScale: 1 / CANVAS_PT_SCALE,
        shouldShowTextFill: isShowTextFill(option.textElement),
        textDirection: this.cellModel.textBodyStyle?.wrapperStyle?.textDirection ?? TextDirectionType.HORIZONTAL,
        slideKey: this.factoryOptions.model.slideRef?.key ?? '',
        filterDisplayOptions: this.factoryOptions.getFilterDisplayOptions?.(),
        // 生成图片场景 && 图形大小超过阈值则禁掉滤镜
        forbidFilter: isCopyToImageOverSize(
            this.factoryOptions.exportImageManager?.isImageCopying,
            option?.logicWidth,
            option?.logicHeight,
        ),
    });
}
